import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ICustomerUser,
  Prescription,
  PrescriptionExt,
  Fulfillment,
  DrawerType,
  IAdminUser,
  RxDrug
} from '@care-portals/api-interfaces';
import { AuthService } from '../../services/auth/auth.service';
import { DrawerStateFacade } from '../../+store/drawer-state/drawer-state.facade';
import { PrescriptionFacade } from '../../+store/prescription/prescription.facade';
import { OrdersFacade } from '../../+store/order/order.facade';
import { DrugFacade } from 'src/app/+store/drug/drug.facade';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'my-health-admin-customer-prescriptions',
  templateUrl: './customer-prescriptions.component.html',
  styleUrls: ['./customer-prescriptions.component.less']
})
export class CustomerPrescriptionsComponent implements OnInit, OnDestroy {
  @Input() customer: ICustomerUser;
  prescriptions: Prescription[];
  tableData: PrescriptionExt[];

  isLoading: boolean;
  isDestroyed = new Subject();
  filters = {
    includeFulfilled: true,
    includeVoided: true
  };
  sendRxCooldowns = new Set<string>();
  sendingPdfToChat = new Set<string>();
  pendingRxToLink: Prescription | null = null;
  pendingTransferAfterLink = false;
  constructor(
    private readonly drawerFacade: DrawerStateFacade,
    private readonly ordersFacade: OrdersFacade,
    private readonly prescriptionFacade: PrescriptionFacade,
    public readonly router: Router,
    public readonly auth: AuthService,
    private readonly drugFacade: DrugFacade,
    private readonly modalService: NzModalService,
    private readonly notification: NzNotificationService
  ) {
    this.drawerFacade.popped$.subscribe(res => {
      if (
        (res?.name === DrawerType.CREATE_RX ||
          res?.name === DrawerType.EDIT_RX ||
          res?.name === DrawerType.CREATE_FULFILL) &&
        res?.props === true
      ) {
        this.updateLastFulfill();
      } else if (
        res?.name === DrawerType.FIND_ORDER &&
        this.pendingRxToLink
      ) {
        const rxToLink = this.pendingRxToLink;
        const shouldTransferAfterLink = this.pendingTransferAfterLink;
        this.pendingRxToLink = null;
        this.pendingTransferAfterLink = false;

        const orderId = res?.props;
        if (Number.isFinite(orderId) && rxToLink?._id) {
          this.prescriptionFacade.updatePrescription(rxToLink._id, {
            linkedOrderId: orderId
          });
          this.notification.success(
            'Linked',
            `RX ${rxToLink._id} was linked to order #${orderId}.`
          );
          if (shouldTransferAfterLink) {
            this.sendPrescriptionPdfToChat(rxToLink._id);
          }
        }
      }
    });

    this.prescriptionFacade.prescriptions$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(res => {
        this.prescriptions = Object.keys(res).map(key => res[key]);
        this.updateLastFulfill();
      });

    this.prescriptionFacade.loading$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(res => {
        this.isLoading = res;
      });

    this.prescriptionFacade.sendPrescriptionPdfToChatCompleted$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(({ rxId }) => this.sendingPdfToChat.delete(rxId));
  }

  ngOnInit() {
    this.prescriptionFacade.loadPrescriptions(
      'newer',
      {
        customerId: this.customer._id,
        ...this.filters
      },
      true
    );

    this.drugFacade.loadDrugs({ status: 'active' });
  }

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }

  loadOlder() {
    this.prescriptionFacade.loadPrescriptions('older', {
      customerId: this.customer._id,
      ...this.filters
    });
  }

  loadNewer() {
    this.prescriptionFacade.loadPrescriptions('newer', {
      customerId: this.customer._id,
      ...this.filters
    });
  }

  updateLastFulfill() {
    if (!this.prescriptions) {
      return;
    }

    this.tableData = this.prescriptions.map(item => {
      const lastFulfill =
        item.fulfillments && item.fulfillments.length > 0
          ? { ...item.fulfillments[item.fulfillments.length - 1] }
          : ({} as Fulfillment);

      const rx: PrescriptionExt = { ...item, lastFulfill, expand: false };
      return rx;
    });
  }

  sendPrescriptionPdfToChat(rxId: string) {
    this.sendingPdfToChat.add(rxId);
    this.prescriptionFacade.sendPrescriptionPdfToPatientChat(rxId);
  }

  transferRx(rx: Prescription) {
    this.modalService.confirm({
      nzTitle: 'Transfer RX',
      nzContent:
        'Sending this RX will transfer it out. Do you want to link an order before transferring?',
      nzOkText: 'Link Order & Transfer',
      nzCancelText: 'Transfer Only',
      nzOnOk: () => {
        const customerId =
          typeof rx.customer === 'string' ? rx.customer : rx.customer?._id;
        if (!customerId) {
          this.notification.error('Error', 'Unable to determine patient for RX.');
          return;
        }
        this.pendingRxToLink = rx;
        this.pendingTransferAfterLink = true;
        this.drawerFacade.openDrawer({
          name: DrawerType.FIND_ORDER,
          props: { customerId }
        });
      },
      nzOnCancel: () => this.sendPrescriptionPdfToChat(rx._id)
    });
  }

  sendRx(rxId: string) {
    const prescription = this.tableData?.find(rx => rx._id === rxId);

    // Check if the latest fulfillment status is 'sent'
    if (prescription?.lastFulfill?.status === 'sent') {
      this.modalService.warning({
        nzTitle: 'Prescription Already Sent',
        nzContent: 'This prescription has already been sent to the pharmacy.',
        nzOkText: 'OK'
      });
      return;
    }

    // Add to cooldown set and remove after 60 seconds
    this.sendRxCooldowns.add(rxId);
    setTimeout(() => this.sendRxCooldowns.delete(rxId), 60000);

    this.prescriptionFacade.sendRx(rxId);
  }

  isOnCooldown(rxId: string): boolean {
    return this.sendRxCooldowns.has(rxId);
  }

  openCreateRx(rx?: Prescription) {
    //clear rx extras and metadata by creating a new object
    const cleanedRx = rx ? { ...rx, extras: {}, meta: {} } : undefined;
    this.drawerFacade.openDrawer({
      name: DrawerType.CREATE_RX,
      props: { customerId: this.customer._id, rx: cleanedRx }
    });
  }

  openEditRx(rx: Prescription) {
    this.drawerFacade.openDrawer({
      name: DrawerType.EDIT_RX,
      props: { rx }
    });
  }

  voidRx(rxId: string): void {
    this.prescriptionFacade.updatePrescription(rxId, { voided: true });
  }

  openFulfillment(rx: Prescription) {
    this.drawerFacade.openDrawer({
      name: DrawerType.CREATE_FULFILL,
      props: { rx: { ...rx } }
    });
  }

  changeStatus(status, prescription: PrescriptionExt) {
    prescription.lastFulfill = { ...prescription.lastFulfill, status };
    const len = prescription.fulfillments.length;
    const newFulfillments = [...prescription.fulfillments];
    newFulfillments[len - 1] = prescription.lastFulfill;

    this.prescriptionFacade.updatePrescription(prescription._id, {
      fulfillments: newFulfillments
    });
  }

  asAdminUser(data) {
    return data as IAdminUser;
  }

  viewRx(rx: Prescription) {
    this.drawerFacade.openDrawer({
      name: DrawerType.PRESCRIPTION_PDF,
      props: {
        rxId: rx._id
      }
    });
  }

  openOrderDetails(orderId: string) {
    this.ordersFacade.openOrderDetails(orderId);
  }

  onDrugClick(drug: RxDrug) {
    this.drawerFacade.openDrawer({
      name: DrawerType.DRUG_DETAIL,
      props: { din: drug.din }
    });
  }
}
